import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Scale, Handshake, Lock } from "lucide-react";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";
import VesselFrame from "@/components/VesselFrame";
import Marquee from "@/components/Marquee";

const IMG = {
  jlt: "https://images.unsplash.com/photo-1607414851776-f2fcc379fb48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxkdWJhaSUyMHNreWxpbmUlMjBqdW1laXJhaCUyMGxha2UlMjB0b3dlcnMlMjBza3lzY3JhcGVyfGVufDB8fHx8MTc5MDQxMzM3NHww&ixlib=rb-4.1.0&q=85",
  skyline: "https://images.unsplash.com/photo-1462007895615-c8c073bebcd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBqdW1laXJhaCUyMGxha2UlMjB0b3dlcnMlMjBza3lzY3JhcGVyfGVufDB8fHx8MTc5MDQxMzM3NHww&ixlib=rb-4.1.0&q=85",
};

const VALUES = [
  { icon: Eye, title: "Discretion", copy: "Mandates are circulated only to qualified, named counterparties. Nothing enters the open market without instruction." },
  { icon: Scale, title: "Accuracy", copy: "Valuations are defended with evidence — comparable sales, earnings and demolition floors — never optimism." },
  { icon: Handshake, title: "Relationships", copy: "Our counterparties are decades in the making: owners, cash buyers, yards and financiers who answer our calls." },
  { icon: Lock, title: "Integrity", copy: "One mandate, one loyalty. We do not trade against a principal's position or hold undisclosed interests." },
];

const NETWORK = [
  { city: "Dubai", role: "Headquarters — Jumeirah Lake Towers" },
  { city: "Singapore", role: "Asia-Pacific tonnage desk" },
  { city: "London", role: "European owners & legal coordination" },
  { city: "Athens", role: "Greek owner relationships" },
  { city: "Alang", role: "Recycling yard liaison" },
  { city: "Chattogram", role: "Recycling yard liaison" },
];

export default function About() {
  useEffect(() => {
    document.title = "About — Dice International LLC";
  }, []);

  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden" data-testid="about-hero">
        <img src={IMG.skyline} alt="Dubai skyline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030F26] via-[#030F26]/75 to-[#030F26]/45" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-20 pt-32 sm:px-10 lg:pt-40">
          <Overline testId="about-overline">About the House</Overline>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="about-heading">
            <MaskedLine delay={0.15}>An established desk in the heart of</MaskedLine>
            <MaskedLine delay={0.3}>
              <em className="text-[#C5A059]">Dubai's maritime quarter.</em>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="about-story-section">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Overline testId="about-story-overline">Our Story</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="about-story-heading">
                Built on tonnage, tempered by cycles.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base leading-relaxed text-slate-300">
                Dice International LLC was founded in Dubai's Jumeirah Lake Towers to serve a simple gap: owners
                and cash buyers who needed a broker that treats a vessel sale — or a final recycling placement —
                with the gravity it deserves.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                From Jumeirah Bay Tower X2, our desk works across time zones with owners in Europe and Asia,
                traders in the Gulf, and recycling facilities across the subcontinent. We have chosen to remain
                deliberately focused: two disciplines, executed to an institutional standard.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                The result is a brokerage that principals instruct repeatedly — because the last transaction was
                handled quietly, priced honestly and closed cleanly.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <VesselFrame src={IMG.jlt} alt="Jumeirah Lake Towers, Dubai" testId="about-jlt-frame" className="h-[420px]" />
              <p className="mt-4 font-mono text-[0.65rem] uppercase tracking-[0.25em] text-slate-500">
                Jumeirah Bay Tower X2 · Cluster X · JLT · Dubai
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <Reveal>
            <Overline testId="about-values-overline">How We Work</Overline>
            <h2 className="mt-6 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="about-values-heading">
              Four principles, held without exception.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-values-grid">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col border border-[#1E3A6E]/60 bg-[#091B3E]/70 p-7 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5 hover:border-[#C5A059]/60">
                  <v.icon className="h-6 w-6 text-[#C5A059]" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-semibold text-white">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="about-network-section">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline testId="about-network-overline">Global Network</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="about-network-heading">
                One desk in Dubai. Doors open everywhere.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Through long-standing partner desks and yard relationships, we place tonnage and negotiate
                recycling terms across the world's principal maritime centres.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7" data-testid="about-network-list">
            {NETWORK.map((n, i) => (
              <Reveal key={n.city} delay={i * 0.05}>
                <div className="flex items-baseline justify-between gap-6 border-t border-[#1E3A6E]/50 py-5 first:border-t-0 first:pt-0">
                  <span className="font-serif text-xl font-semibold text-white sm:text-2xl">{n.city}</span>
                  <span className="text-right font-mono text-xs uppercase tracking-[0.18em] text-slate-400">{n.role}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="about-pledge-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-28">
          <Reveal>
            <div className="mx-auto max-w-3xl border border-[#C5A059]/40 bg-[#091B3E]/70 p-10 backdrop-blur-xl sm:p-14" data-testid="about-pledge-card">
              <Overline testId="about-pledge-overline">Our Pledge</Overline>
              <p className="mt-6 font-serif text-2xl font-medium leading-snug text-white sm:text-3xl">
                "Every mandate is handled as if our name — not our client's — were on the headline. Confidential
                when you need quiet, relentless when you need a result."
              </p>
              <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-[#C5A059]">
                The Broking Desk · Dice International LLC
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  data-testid="about-pledge-cta"
                  className="group inline-flex items-center gap-3 bg-[#C5A059] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#030F26] transition-colors duration-300 hover:bg-[#E2C08D]"
                >
                  Start a Conversation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
